import { FC, useContext, useEffect, useRef, useSyncExternalStore } from 'react';
import { MainContext } from 'contexts/MainContext';
import { saveProposal } from 'utils/shared/idbUrl';
import { applyProposalCurrency } from 'utils/shared/proposal';
import { Proposal } from 'ts/shared/interfaces/CreateProposalParams';
import {
  applyProposalDataAction,
  drainProposalActions,
  enqueueProposalUi,
  getProposalActionsVersion,
  ProposalItemRef,
  ProposalUiIntent,
  setProposalItemsForAssistant,
  subscribeProposalActions,
} from './proposalActions';

let proposalActionChain: Promise<void> = Promise.resolve();

interface ProposalAssistantBridgeProps {
  setActiveTab: (tab: number) => void;
}

const ProposalAssistantBridge: FC<ProposalAssistantBridgeProps> = ({ setActiveTab }) => {
  const { proposal, setProposal } = useContext(MainContext);
  const proposalRef = useRef(proposal);
  const writtenRef = useRef<Proposal | null>(null);
  const setActiveTabRef = useRef(setActiveTab);
  const setProposalRef = useRef(setProposal);
  const pendingUiRef = useRef<ProposalUiIntent[]>([]);
  setActiveTabRef.current = setActiveTab;
  setProposalRef.current = setProposal;

  if (writtenRef.current === proposal) {
    writtenRef.current = null;
  }
  if (!writtenRef.current) {
    proposalRef.current = proposal;
  }

  const version = useSyncExternalStore(subscribeProposalActions, getProposalActionsVersion);

  useEffect(() => {
    const items: ProposalItemRef[] = [
      ...proposal.hotels.map((hotel, index) => ({
        name: hotel.item?.name || hotel.item?.item?.name || '',
        product: 'hotels' as const,
        index: index + 1,
      })),
      ...proposal.flights.map((flight, index) => ({
        name: [
          (flight.item as any)?.outbound?.gdsFlyAirs?.[0]?.airline?.code,
          (flight.item as any)?.outbound?.gdsFlyAirs?.[0]?.flightNumber,
        ]
          .filter(Boolean)
          .join(' '),
        product: 'flights' as const,
        index: index + 1,
      })),
      ...proposal.dynamic.map((item, index) => ({
        name: item.hotel?.item?.name || '',
        product: 'dynamic' as const,
        index: index + 1,
      })),
      ...proposal.transfers.map((_, index) => ({
        name: `Transfer ${index + 1}`,
        product: 'transfers' as const,
        index: index + 1,
      })),
      ...proposal.ilPackages.map((pac, index) => ({
        name: pac.item?.info?.hotelName || pac.item?.deal?.rowName || '',
        product: 'packages' as const,
        index: index + 1,
      })),
    ].filter((item) => item.name);
    setProposalItemsForAssistant(items);
  }, [proposal]);

  useEffect(() => {
    const actions = drainProposalActions();
    if (!actions.length) {
      return;
    }

    proposalActionChain = proposalActionChain
      .then(async () => {
        let next = proposalRef.current;
        let changed = false;
        let persist = false;
        let openItem = false;
        let pageTab: number | null = null;
        const ui: ProposalUiIntent[] = [];

        for (const action of actions) {
          if (action.action === 'page-tab') {
            pageTab = action.tab === 'search' ? 1 : action.tab === 'trip' ? 2 : 0;
            continue;
          }
          if (action.action === 'currency' && action.currency) {
            try {
              next = await applyProposalCurrency(next, action.currency);
              changed = true;
            } catch {
              // Footer still switches even if rate conversion fails.
            }
            sessionStorage.setItem('displayCurrencyHotels', action.currency);
            ui.push({ kind: 'customize', currency: action.currency });
            continue;
          }

          const applied = applyProposalDataAction(next, action);
          if (applied.proposal) {
            next = applied.proposal;
            changed = true;
          }
          if (applied.persist) {
            persist = true;
          }
          if (applied.openItemTab) {
            openItem = true;
          }
          ui.push(...applied.ui);
        }

        if (pageTab != null) {
          setActiveTabRef.current(pageTab);
        } else if (openItem) {
          setActiveTabRef.current(0);
        }

        if (ui.length) {
          pendingUiRef.current.push(...ui);
        }
        if (changed) {
          writtenRef.current = next;
          proposalRef.current = next;
          setProposalRef.current(next);
        }
        if (pendingUiRef.current.length) {
          enqueueProposalUi(pendingUiRef.current.splice(0, pendingUiRef.current.length));
        }
        if (persist) {
          void saveProposal('proposal', next);
        }
      })
      .catch(() => undefined);
  }, [version]);

  return null;
};

export default ProposalAssistantBridge;
